import useSWR from "swr";
import { useApiClient } from "@/hooks/useApiClient";
import { SAMBO_KUN_PARTNER_ID } from "@/features/partner/constants";
import { getPartnerLimit } from "@/features/partner/api/getPartnerLimit";
import { type GetPartnerLimitResponse } from "@/features/partner/types";

/** ポーリング間隔（ミリ秒）。値変更の即時反映を必須としないため 5 分とする。 */
const POLLING_INTERVAL_MS = 5 * 60 * 1000;

interface UseSamboKunLimitOptions {
  /**
   * 参謀くん UI が表示される場合のみフェッチさせるためのフラグ。
   * `useShowSendToSamboKun()` の戻り値をそのまま渡すユースケースを想定。
   *
   * 未指定または true の場合は常にフェッチする（既存挙動）。
   * false の場合は SWR キーを null にしてリクエストを送らない。
   */
  enabled?: boolean;
}

interface UseSamboKunLimitResult {
  /** 上限件数（API 取得成功時のみ値が入る）。エラー時 / 未フェッチ時は null。 */
  maxPropertiesPerRequest: number | null;
  /** 取得中フラグ（初回ロード時 true）。未フェッチ時は false。 */
  isLoading: boolean;
  /** API が現在エラー状態かどうか（FE 側ボタン Disable 判定に利用）。 */
  isError: boolean;
}

/**
 * 参謀くんの連携上限件数を SWR で取得する hook（一定間隔でポーリング）。
 *
 * - 取得成功時: `maxPropertiesPerRequest` に値、`isError=false`
 * - 取得失敗時: `maxPropertiesPerRequest=null`、`isError=true`
 *   （呼び出し側はこの場合「全ボタン Disable」とする）
 * - `enabled=false` 時: フェッチしない、`maxPropertiesPerRequest=null` / `isError=false` / `isLoading=false`
 *
 * @param options.enabled - 参謀くん UI 表示時のみ true を渡すことで不要なリクエストを抑止できる
 * @returns 上限件数の取得結果
 */
export const useSamboKunLimit = (
  options: UseSamboKunLimitOptions = {}
): UseSamboKunLimitResult => {
  const { enabled = true } = options;
  const { apiClient } = useApiClient();

  const { data, error, isLoading } = useSWR<GetPartnerLimitResponse, unknown>(
    enabled ? `/partner/${SAMBO_KUN_PARTNER_ID}/limit` : null,
    async () => await getPartnerLimit(apiClient, SAMBO_KUN_PARTNER_ID),
    {
      refreshInterval: POLLING_INTERVAL_MS,
      revalidateOnFocus: true,
      shouldRetryOnError: true,
    }
  );

  return {
    maxPropertiesPerRequest: data?.maxPropertiesPerRequest ?? null,
    isLoading,
    isError: error != null,
  };
};
